import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Circle, ClipboardList, Plug } from "lucide-react";

/**
 * Tasks — operational to-dos/reminders created via the ChatGPT connector
 * (drafted in ChatGPT, confirmed in-app). Shows the signed-in user's tasks.
 */
export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/connector/tasks");
      setTasks(res.data?.tasks || []);
    } catch (_e) {
      toast.error("Couldn't load tasks");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    try {
      await api.post(`/connector/tasks/${t.id}/done`);
      load();
    } catch (_e) {
      toast.error("Could not update task");
    }
  };

  return (
    <div data-testid="tasks-page">
      <PageHeader
        eyebrow="Operations"
        title="Tasks"
        subtitle="Reminders and to-dos you've captured — including ones drafted in ChatGPT and confirmed here."
        actions={
          <Button asChild variant="outline" data-testid="tasks-connector-link">
            <Link to="/connector"><Plug className="h-4 w-4 mr-1" /> ChatGPT connector</Link>
          </Button>
        }
      />

      {loading ? (
        <div className="py-20 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : tasks.length === 0 ? (
        <Card className="rounded-xl">
          <CardContent className="pt-10 pb-10 text-center space-y-3" data-testid="tasks-empty">
            <ClipboardList className="h-10 w-10 text-muted-foreground mx-auto" />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              No tasks yet. Ask ChatGPT to schedule something (e.g. “book the vet for Bella”) and confirm it here.
            </p>
            <Button asChild variant="outline"><Link to="/connector">Set up the ChatGPT connector</Link></Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="tasks-list">
          {tasks.map((t) => {
            const done = t.status === "done";
            return (
              <Card key={t.id} className="rounded-xl" data-testid={`task-${t.id}`}>
                <CardContent className="py-3 flex items-start gap-3">
                  <button onClick={() => toggle(t)} className="mt-0.5 shrink-0" data-testid={`task-toggle-${t.id}`} aria-label={done ? "Mark as open" : "Mark as done"}>
                    {done
                      ? <CheckCircle2 className="h-5 w-5 text-[hsl(var(--ap-forest))]" />
                      : <Circle className="h-5 w-5 text-muted-foreground" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-medium ${done ? "line-through text-muted-foreground" : ""}`}>{t.title}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                      {t.task_type && <Badge variant="secondary">{t.task_type}</Badge>}
                      {t.horse_name && (
                        t.horse_id
                          ? <Link to={`/horses/${t.horse_id}`} className="hover:underline">{t.horse_name}</Link>
                          : <span>{t.horse_name}</span>
                      )}
                      {t.due_at && <span>· due {t.due_at}</span>}
                      {t.source === "chatgpt_connector" && <span>· via ChatGPT</span>}
                    </div>
                    {t.notes && <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{t.notes}</p>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
